import { memo, useMemo, useRef } from 'react'
import { Link } from 'react-router-dom'
import constants from 'app/constants'
import { useRootContext } from 'app/services/RootProvider/RootProvider'
import useAppData from 'app/services/hooks/useAppData'
import ButtonAccountStatus from 'components/elements/ButtonAccountStatus/ButtonAccountStatus'
import useScrollOverflow from 'lib/react/hooks/useScrollOverflow'
import ButtonBurger from '../ButtonBurger/ButtonBurger'
import ButtonCart from '../ButtonCart/ButtonCart'
import Logo from '../Logo/Logo'
import useHandleNavClickLink from '../hooks/useHandleNavClickLink'
import useRenderNavLinks from '../hooks/useRenderNavLinks'
import styles from './Header.module.scss'
import NavList from './NavList/NavList'
import useHeaderAnimation from './hooks/useHeaderAnimation'

/**
 * The `Header` of the site
 *
 * @returns {React.ReactElement} the element
 */
function Header () {
  const { headerMode, isHeaderActive } = useRootContext()
  const ref = useRef()
  const windowRef = useRef(window)
  const { hasOverflowTop } = useScrollOverflow(windowRef, 50)
  const appData = useAppData()
  const linksData = useMemo(() => appData?.global?.nav?.links || [], [appData])
  const isVisible = useHeaderAnimation(ref, isHeaderActive)
  // Handle link actions
  const handleClickLink = useHandleNavClickLink()

  const attributes = useMemo(
    () => ({
      className: [
        styles.header,
        isVisible && styles.visible,
        (hasOverflowTop || headerMode === 'solid') && styles.overflow
      ].join(' ')
    }),
    [headerMode, isVisible, hasOverflowTop]
  )

  const linksList = useRenderNavLinks(linksData, NavList.Item, handleClickLink)

  return (
    <header {...attributes}>
      <div ref={ref} className={styles.headerWrapper}>
        <div className={styles.headerContent}>
          <Link className={styles.headerTitle} to="/">
            <Logo.Standard
              className={styles.headerLogo}
              isActive={true}
              isBeta={constants.IS_BETA}
            />
          </Link>

          {constants.MODE !== 0 && (
            <NavList className={styles.headerNav}>{linksList}</NavList>
          )}
          <div className={styles.headerActions}>
            <ButtonBurger className={styles.headerBurger} />
            <ButtonAccountStatus useHighlightHover={true} />
            {constants.IS_MARKETPLACE_ENABLED && <ButtonCart />}
          </div>
        </div>
      </div>
    </header>
  )
}

export default memo(Header)
